import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './MentorValidationResultsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function getItemType(item) {
  const hasContent = item.text != null || item.correct_answer != null || (item.category != null && item.category !== '');
  if (item.question_id == null) return 'add';
  if (!hasContent) return 'delete';
  return 'edit';
}

function MentorValidationResultsSubTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterValidationId, setFilterValidationId] = useState('');
  const [filterMentorId, setFilterMentorId] = useState('');
  const [filterTopicId, setFilterTopicId] = useState('');
  const [applyModal, setApplyModal] = useState(null);
  const [applyAwardPoints, setApplyAwardPoints] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [applyForm, setApplyForm] = useState({ text: '', correct_answer: '', category: 'Junior' });
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [deletingValidationId, setDeletingValidationId] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterValidationId) params.set('validation_id', filterValidationId);
      if (filterMentorId) params.set('mentor_id', filterMentorId);
      if (filterTopicId) params.set('topic_id', filterTopicId);
      const url = `${API_URL}/api/mentor-validation-result-items${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url);
      setItems(res.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке: ' + (err.response?.data?.detail || err.message));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterValidationId, filterMentorId, filterTopicId]);

  const openApplyModal = (item, group, type) => {
    setApplyModal({ item, group, type });
    setCurrentQuestion(null);
    setApplyError(null);
    setApplyAwardPoints(type === 'add' || type === 'edit');
    setApplyForm({
      text: item.text != null ? item.text : '',
      correct_answer: item.correct_answer != null ? item.correct_answer : '',
      category: item.category != null && item.category !== '' ? item.category : 'Junior',
    });
    if (type === 'delete' || type === 'edit') {
      setLoadingQuestion(true);
      axios
        .get(`${API_URL}/api/questions/${item.question_id}`)
        .then((res) => setCurrentQuestion(res.data))
        .catch(() => setCurrentQuestion(null))
        .finally(() => setLoadingQuestion(false));
    }
  };

  const closeApplyModal = () => {
    setApplyModal(null);
    setCurrentQuestion(null);
    setApplyError(null);
  };

  const deleteDiffItem = async (itemId) => {
    await axios.delete(`${API_URL}/api/mentor-validation-result-items/${itemId}`);
  };

  const deleteAllForValidation = async (validationId) => {
    if (!window.confirm(`Удалить все записи diff для валидации #${validationId}?`)) return;
    setDeletingValidationId(validationId);
    try {
      await axios.delete(`${API_URL}/api/mentor-validation-result-items/by-validation/${validationId}`);
      fetchData();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingValidationId(null);
    }
  };

  const handleDeleteSingleDiff = async (itemId) => {
    if (!window.confirm('Удалить эту запись diff из таблицы?')) return;
    setDeletingItemId(itemId);
    try {
      await deleteDiffItem(itemId);
      fetchData();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleApplyDelete = async () => {
    if (!applyModal || applyModal.type !== 'delete') return;
    setApplying(true);
    setApplyError(null);
    try {
      await axios.delete(`${API_URL}/api/questions/${applyModal.item.question_id}`);
      await deleteDiffItem(applyModal.item.id);
      closeApplyModal();
      fetchData();
    } catch (err) {
      setApplyError(err.response?.data?.detail || err.message || 'Ошибка удаления');
    } finally {
      setApplying(false);
    }
  };

  const awardPoints = async (mentorId, questionId, points, transactionType) => {
    await axios.post(`${API_URL}/api/mentor-validation-points/award`, {
      mentor_id: mentorId,
      question_id: questionId ?? null,
      points: points ?? 1,
      transaction_type: transactionType || 'add',
    });
  };

  const handleApplyEdit = async () => {
    if (!applyModal || applyModal.type !== 'edit') return;
    const { text, correct_answer, category } = applyForm;
    if (!text || !text.trim()) {
      setApplyError('Введите текст вопроса');
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      await axios.put(`${API_URL}/api/questions/${applyModal.item.question_id}`, {
        text: text.trim(),
        correct_answer: correct_answer.trim() || null,
        category: category || null,
      });
      if (applyAwardPoints && applyModal.group.mentor_id) {
        await awardPoints(applyModal.group.mentor_id, applyModal.item.question_id, 1, 'edit');
      }
      await deleteDiffItem(applyModal.item.id);
      closeApplyModal();
      fetchData();
    } catch (err) {
      setApplyError(err.response?.data?.detail || err.message || 'Ошибка обновления');
    } finally {
      setApplying(false);
    }
  };

  const handleApplyAdd = async () => {
    if (!applyModal || applyModal.type !== 'add') return;
    const topicId = applyModal.group.topic_id;
    if (topicId == null) {
      setApplyError('Не указана тема валидации');
      return;
    }
    const { text, correct_answer, category } = applyForm;
    if (!text || !text.trim()) {
      setApplyError('Введите текст вопроса');
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      const res = await axios.post(`${API_URL}/api/questions`, {
        topic_id: topicId,
        text: text.trim(),
        correct_answer: correct_answer.trim() || null,
        category: category || null,
      });
      if (applyAwardPoints && applyModal.group.mentor_id && res.data?.id) {
        await awardPoints(applyModal.group.mentor_id, res.data.id, 1, 'add');
      }
      await deleteDiffItem(applyModal.item.id);
      closeApplyModal();
      fetchData();
    } catch (err) {
      setApplyError(err.response?.data?.detail || err.message || 'Ошибка создания');
    } finally {
      setApplying(false);
    }
  };

  const groupedByValidation = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const key = item.validation_id;
      if (!map[key]) {
        map[key] = {
          validation_id: key,
          mentor_login: item.validation_mentor_login,
          mentor_id: item.validation_mentor_id,
          topic_title: item.validation_topic_title,
          topic_id: item.validation_topic_id,
          items: [],
        };
      }
      map[key].items.push(item);
    });
    return Object.values(map).sort((a, b) => b.validation_id - a.validation_id);
  }, [items]);

  const modalTitle =
    applyModal &&
    (applyModal.type === 'delete'
      ? 'Применить: удаление вопроса'
      : applyModal.type === 'edit'
        ? 'Применить: изменение вопроса'
        : 'Применить: добавление вопроса');

  if (loading) {
    return <div className="validation-results-loading">Загрузка...</div>;
  }

  return (
    <div className="mentor-validation-results-sub-tab">
      <div className="validation-results-header">
        <h2>Результаты валидаций (diff)</h2>
        <button className="btn btn-secondary" onClick={fetchData}>
          Обновить
        </button>
      </div>

      <div className="validation-results-filters">
        <input
          type="number"
          placeholder="ID валидации"
          value={filterValidationId}
          onChange={(e) => setFilterValidationId(e.target.value)}
          className="filter-input"
        />
        <input
          type="number"
          placeholder="ID ментора"
          value={filterMentorId}
          onChange={(e) => setFilterMentorId(e.target.value)}
          className="filter-input"
        />
        <input
          type="number"
          placeholder="ID темы"
          value={filterTopicId}
          onChange={(e) => setFilterTopicId(e.target.value)}
          className="filter-input"
        />
      </div>

      {error && <div className="validation-results-error">{error}</div>}

      {groupedByValidation.length === 0 ? (
        <div className="validation-results-empty">Нет записей результатов валидаций.</div>
      ) : (
        <div className="validation-results-groups">
          {groupedByValidation.map((group) => (
            <div key={group.validation_id} className="validation-result-group">
              <div className="validation-result-group-header">
                <h3 className="validation-result-group-title">
                  Валидация #{group.validation_id} — {group.mentor_login || `ментор ${group.mentor_id}`}, тема: {group.topic_title || group.topic_id}
                </h3>
                <button
                  type="button"
                  className="btn btn-danger btn-delete-all"
                  onClick={() => deleteAllForValidation(group.validation_id)}
                  disabled={deletingValidationId === group.validation_id}
                  title="Удалить все записи diff этой валидации"
                >
                  {deletingValidationId === group.validation_id ? 'Удаление…' : 'Удалить все данные валидации'}
                </button>
              </div>
              <div className="validation-result-items">
                {group.items.map((item) => {
                  const type = getItemType(item);
                  const typeLabel = type === 'add' ? 'Добавлен' : type === 'delete' ? 'Удалён' : 'Изменён';
                  return (
                    <div key={item.id} className={`validation-result-item validation-result-item--${type}`}>
                      <div className="validation-result-item-meta">
                        <span className="validation-result-item-type">{typeLabel}</span>
                        {item.question_id != null && (
                          <span className="validation-result-item-id">question_id: {item.question_id}</span>
                        )}
                        {item.question_text_preview && (
                          <span className="validation-result-item-preview" title={item.question_text_preview}>
                            {item.question_text_preview}
                          </span>
                        )}
                      </div>
                      {(type === 'add' || type === 'edit') && (
                        <div className="validation-result-item-fields">
                          {item.category != null && item.category !== '' && (
                            <div><strong>Категория:</strong> {item.category}</div>
                          )}
                          {item.text != null && item.text !== '' && (
                            <div><strong>Текст:</strong> <pre className="validation-result-text">{item.text}</pre></div>
                          )}
                          {item.correct_answer != null && item.correct_answer !== '' && (
                            <div><strong>Ответ:</strong> <pre className="validation-result-answer">{item.correct_answer}</pre></div>
                          )}
                        </div>
                      )}
                      {type === 'delete' && (
                        <div className="validation-result-item-fields">Вопрос удалён (пустые поля)</div>
                      )}
                      <div className="validation-result-item-actions">
                        <button
                          type="button"
                          className="btn btn-primary btn-apply"
                          onClick={() => openApplyModal(item, group, type)}
                        >
                          Применить изменения
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-delete-diff"
                          onClick={() => handleDeleteSingleDiff(item.id)}
                          disabled={deletingItemId === item.id}
                          title="Удалить запись diff из таблицы"
                        >
                          {deletingItemId === item.id ? '…' : 'Удалить запись'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {applyModal && (
        <div className="validation-apply-overlay" onClick={closeApplyModal}>
          <div className="validation-apply-modal" onClick={(e) => e.stopPropagation()}>
            <div className="validation-apply-header">
              <h3>{modalTitle}</h3>
              <button type="button" className="validation-apply-close" onClick={closeApplyModal} aria-label="Закрыть">
                ×
              </button>
            </div>
            <div className="validation-apply-body">
              {applyError && <div className="validation-results-error">{applyError}</div>}

              {applyModal.type === 'delete' && (
                <>
                  {loadingQuestion ? (
                    <p>Загрузка данных вопроса...</p>
                  ) : currentQuestion ? (
                    <div className="validation-apply-form validation-apply-delete">
                      <div className="apply-field">
                        <label>ID вопроса</label>
                        <div className="apply-value">{applyModal.item.question_id}</div>
                      </div>
                      <div className="apply-field">
                        <label>Текст вопроса</label>
                        <pre className="apply-value apply-value-pre">{currentQuestion.text}</pre>
                      </div>
                    </div>
                  ) : (
                    <p className="apply-not-found">Вопрос не найден (возможно уже удалён).</p>
                  )}
                </>
              )}

              {applyModal.type === 'edit' && (
                <>
                  {loadingQuestion ? (
                    <p>Загрузка данных вопроса...</p>
                  ) : currentQuestion ? (
                    <div className="validation-apply-form validation-apply-edit">
                      <div className="apply-section">
                        <h4>Текущие данные (в БД)</h4>
                        <div className="apply-field">
                          <label>Текст вопроса</label>
                          <pre className="apply-value apply-value-pre apply-readonly">{currentQuestion.text}</pre>
                        </div>
                        <div className="apply-field">
                          <label>Правильный ответ</label>
                          <pre className="apply-value apply-value-pre apply-readonly">{currentQuestion.correct_answer || '—'}</pre>
                        </div>
                        <div className="apply-field">
                          <label>Категория</label>
                          <div className="apply-value apply-readonly">{currentQuestion.category || '—'}</div>
                        </div>
                      </div>
                      <div className="apply-section">
                        <h4>Новые данные (будут применены)</h4>
                        <div className="apply-field">
                          <label>Текст вопроса *</label>
                          <textarea
                            className="apply-input"
                            value={applyForm.text}
                            onChange={(e) => setApplyForm((f) => ({ ...f, text: e.target.value }))}
                            rows={4}
                          />
                        </div>
                        <div className="apply-field">
                          <label>Правильный ответ</label>
                          <textarea
                            className="apply-input"
                            value={applyForm.correct_answer}
                            onChange={(e) => setApplyForm((f) => ({ ...f, correct_answer: e.target.value }))}
                            rows={3}
                          />
                        </div>
                        <div className="apply-field">
                          <label>Категория</label>
                          <select
                            className="apply-input"
                            value={applyForm.category}
                            onChange={(e) => setApplyForm((f) => ({ ...f, category: e.target.value }))}
                          >
                            <option value="Junior">Junior</option>
                            <option value="Middle">Middle</option>
                            <option value="Senior">Senior</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="apply-not-found">Вопрос не найден.</p>
                  )}
                </>
              )}

              {applyModal.type === 'add' && (
                <div className="validation-apply-form validation-apply-add">
                  <div className="apply-field">
                    <label>Тема (привязка)</label>
                    <div className="apply-value">{applyModal.group.topic_title || `ID ${applyModal.group.topic_id}`}</div>
                  </div>
                  <div className="apply-field">
                    <label>Категория *</label>
                    <select
                      className="apply-input"
                      value={applyForm.category}
                      onChange={(e) => setApplyForm((f) => ({ ...f, category: e.target.value }))}
                    >
                      <option value="Junior">Junior</option>
                      <option value="Middle">Middle</option>
                      <option value="Senior">Senior</option>
                    </select>
                  </div>
                  <div className="apply-field">
                    <label>Текст вопроса *</label>
                    <textarea
                      className="apply-input"
                      value={applyForm.text}
                      onChange={(e) => setApplyForm((f) => ({ ...f, text: e.target.value }))}
                      rows={4}
                      placeholder="Введите текст вопроса"
                    />
                  </div>
                  <div className="apply-field">
                    <label>Правильный ответ</label>
                    <textarea
                      className="apply-input"
                      value={applyForm.correct_answer}
                      onChange={(e) => setApplyForm((f) => ({ ...f, correct_answer: e.target.value }))}
                      rows={3}
                      placeholder="Введите правильный ответ"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="validation-apply-footer">
              {(applyModal.type === 'add' || applyModal.type === 'edit') && (
                <label className="validation-apply-award-checkbox">
                  <input
                    type="checkbox"
                    checked={applyAwardPoints}
                    onChange={(e) => setApplyAwardPoints(e.target.checked)}
                  />
                  Начислить очко ментору за применение
                </label>
              )}
              <div className="validation-apply-footer-actions">
                <button type="button" className="btn btn-secondary" onClick={closeApplyModal}>
                  Отмена
                </button>
                {applyModal.type === 'delete' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleApplyDelete}
                    disabled={applying || loadingQuestion || !currentQuestion}
                  >
                    {applying ? 'Применяю…' : 'Применить'}
                  </button>
                )}
                {applyModal.type === 'edit' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleApplyEdit}
                    disabled={applying || loadingQuestion || !currentQuestion}
                  >
                    {applying ? 'Применяю…' : 'Применить'}
                  </button>
                )}
                {applyModal.type === 'add' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleApplyAdd}
                    disabled={applying}
                  >
                    {applying ? 'Применяю…' : 'Применить'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MentorValidationResultsSubTab;
